import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class OpsErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("OpsErrorBoundary caught an error:", error, errorInfo);
    }

    public render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center p-8 text-center h-full min-h-[200px] border rounded-lg bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900">
                    <AlertCircle className="h-10 w-10 text-red-500 mb-4" />
                    <h2 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">Something went wrong</h2>
                    <p className="text-sm text-red-600 dark:text-red-300 max-w-md mb-4 bg-white/50 dark:bg-black/20 p-2 rounded font-mono text-left w-full overflow-hidden text-ellipsis">
                        {this.state.error?.message}
                    </p>
                    <Button
                        variant="primary"
                        onClick={() => this.setState({ hasError: false, error: null })}
                    >
                        Try Again
                    </Button>
                </div>
            );
        }

        return this.props.children;
    }
}
